
import { defineComponent, onMounted, Ref, ref } from 'vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import { download, isBlob } from '@/views/utils'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { hasMessage, isAxiosError } from '@/repositories/errorHandler'
import { apiErrorMessage } from '@/apiErrorMessage'
import { callApiWrapper } from '@/views/callApiWrapper'
import { keieiJohoRepositories } from '@/repositories/keiei-joho/keieiJohoRepositories'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { ResponseSonekiKeisanExportLayout, SonekiKeisanKmkType } from '@/repositories/keiei-joho/soneki-keisan/data'
import { corporationTypeType, CorporationTypeType } from '@/views/corporation-info/types'
import SonekiKeisanExportItem from '@/views/keiei-joho/soneki-keisan/SonekiKeisanExportItem.vue'
import { sonekiKeisanKmkTypeType } from '@/repositories/keiei-joho/data'

const repository = keieiJohoRepositories.sonekiKeisanExport

function inputCheck (sonekiItems: SonekiKeisanKmkType[], requestError: Ref<string | null>) {
  const honraiJigyoShueki = sonekiItems.find(v => v.kmkType === sonekiKeisanKmkTypeType.HonraiJigyoShueki)?.kin ?? null
  if (honraiJigyoShueki === null) {
    requestError.value = '事業損益 - 本来業務事業損益 - 事業収益は必須です。'
    return false
  }

  const honraiJigyoHiyo = sonekiItems.find(v => v.kmkType === sonekiKeisanKmkTypeType.HonraiJigyoHiyo)?.kin ?? null
  if (honraiJigyoHiyo === null) {
    requestError.value = '事業損益 - 本来業務事業損益 - 事業費用は必須です。'
    return false
  }

  // NOTE: 本来業務事業利益 と 事業利益 も必須項目だが実質nullになることが無い為、チェックしない

  return true
}

export default defineComponent({
  components: {
    SonekiKeisanExportItem,
    ResponseResultAlert,
    LoadingButton,
    ContentLayout
  },
  setup () {
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const requestError = ref<string | null>(null)
    const hasError = ref<boolean>(false)
    const store = useStore()
    const router = useRouter()

    const accountingStartDate = ref('')
    const accountingEndDate = ref('')
    accountingStartDate.value = store.state.accountingStartDate
    accountingEndDate.value = store.state.accountingEndDate

    // 科目情報
    const sonekiItems = ref<SonekiKeisanKmkType[]>([])

    // 法人情報
    const corporationName = ref('')
    const location = ref('')
    const medicalCorporationNumber = ref('')
    const corporationType = ref<CorporationTypeType>(corporationTypeType.Type1)

    // 初期値設定
    onMounted(async () => {
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const hasCorporationInfo = await repository.checkCorporationInfoExists()
        if (!hasCorporationInfo) {
          requestError.value = '法人情報が登録されていません。法人情報画面から登録してください。'
          loading.value = false
          hasError.value = true
          return
        }

        const data = await repository.getKmkTypes()
        if (data.corporationInfo.corporationType === corporationTypeType.Type6) {
          window.alert('法人情報の法人タイプに「地域医療連携推進法人」が選択されているため、本機能はご利用できません。')
          await router.push('/top')
        }
        refreshUi(data)
      })
      loading.value = false
    })

    /**
     * レスポンス結果の非表示
     */
    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    /**
     * データを画面に反映する
     */
    const refreshUi = (data: ResponseSonekiKeisanExportLayout) => {
      sonekiItems.value = data.kmkTypes
      corporationName.value = data.corporationInfo.corporationName
      location.value = data.corporationInfo.location
      medicalCorporationNumber.value = data.corporationInfo.medicalCorporationNumber
      corporationType.value = data.corporationInfo.corporationType
    }

    /**
     * 登録ボタン
     */
    const onClickedRegister = async () => {
      onDismissAlert()
      if (!inputCheck(sonekiItems.value, requestError)) return
      loading.value = true

      await callApiWrapper(requestError, async () => {
        await repository.save(sonekiItems.value)
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    /**
     * 再集計ボタン
     */
    const onClickedRecalculate = async () => {
      onDismissAlert()
      loading.value = true
      await callApiWrapper(requestError, async () => {
        const data = await repository.recalculate()
        refreshUi(data)
      })
      loading.value = false
    }

    /**
     * 出力ボタン
     */
    const onClickedExport = async () => {
      const result = window.confirm('編集内容を登録してからCSVを出力しますか？\n（OK: 登録して出力、キャンセル: 出力のみ）')
      if (result) {
        await onClickedRegister()
        if (requestError.value !== null) return
      }

      onDismissAlert()
      loading.value = true
      try {
        const response = await repository.export()
        download(response)
      } catch (e) {
        if (isAxiosError(e)) {
          if (e.response && e.response.status >= 400 && e.response.headers['content-type'] === 'application/json') {
            if (isBlob(e.response.data)) {
              const json = await e.response.data.text()
              const jsonObj = JSON.parse(json)
              if (hasMessage(jsonObj)) {
                requestError.value = jsonObj.message
              }
            }
          }
        }
        if (requestError.value === null) {
          requestError.value = apiErrorMessage.badRequest
        }
      } finally {
        loading.value = false
      }
    }

    return {
      loading,
      isSucceedRequest,
      requestError,
      hasError,

      sonekiItems,

      corporationName,
      location,
      medicalCorporationNumber,
      corporationType,
      accountingStartDate,
      accountingEndDate,

      onDismissAlert,
      onClickedRegister,
      onClickedRecalculate,
      onClickedExport
    }
  }
})
